import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import {
  LayoutDashboard,
  Settings,
  Package,
  Ticket,
  Wrench,
  Warehouse,
  QrCode,
  BarChart3,
  Info,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Calendar,
  Activity,
  Users,
  Building2,
  MapPin,
  FileText,
  Cog,
  Database,
  Mail,
  Bell,
  Shield,
  Award,
  ClipboardList,
  AlertTriangle,
  Truck,
  Layers,
  List,
  ShoppingCart,
  Clock,
  HelpCircle,
  Plus,
  Briefcase,
  TrendingUp,
  Archive,
  History,
  Tag,
  Tags,
  Monitor,
  Gauge,
  Battery,
  Grid3X3,
  Search,
  Volume2,
  ClipboardCheck,
  BookOpen,
  Trash2,
  MessageSquare,
  FolderOpen
} from 'lucide-react';
import { Button } from '../ui/button';
import NotificationBell from './NotificationBell';
import PWAInstallPrompt from './PWAInstallPrompt';
import AlertSplashScreen from './AlertSplashScreen';
import QuickStockSearch from './QuickStockSearch';
import { useAlertSound } from '../../hooks/useAlertSound';
import usePushSubscription from '../../hooks/usePushSubscription';
import { useRealtime } from '../../contexts/RealtimeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

const MainLayout = () => {
  const { user, logout, hasRole, hasPermission } = useAuth();
  const { plants, selectedPlant, setSelectedPlant, fetchPlants, fetchLocations, locations, selectedLocation, setSelectedLocation } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  useAlertSound(); // Real-time sound alerts via WebSocket
  usePushSubscription(); // Web Push notifications for mobile/background
  const { connected: wsConnected } = useRealtime();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pmOverdueCount, setPmOverdueCount] = useState(0);
  const [batteryAlertCount, setBatteryAlertCount] = useState(0);
  const [crossDeptUnread, setCrossDeptUnread] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState({
    nomenclatoare: true,
    admin: false,
    maintenance: true,
    wms: false,
    reports: false
  });

  useEffect(() => {
    fetchPlants();
    fetchLocations(); // Load all locations initially
  }, [fetchPlants, fetchLocations]);

  // Fetch PM overdue count for sidebar badge
  useEffect(() => {
    const token = localStorage.getItem('maintix_token');
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const fetchOverdue = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pm/overdue-count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPmOverdueCount(data.overdue_count || 0);
        }
      } catch {}
      try {
        const res = await fetch(`${API_URL}/api/battery-service/alerts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBatteryAlertCount(data.total_needing_attention || 0);
        }
      } catch {}
      try {
        const res = await fetch(`${API_URL}/api/cross-dept-tasks/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCrossDeptUnread(data.my_received_unread || 0);
        }
      } catch {}
    };
    fetchOverdue();
    const interval = setInterval(fetchOverdue, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedPlant && selectedPlant !== 'all') {
      fetchLocations(selectedPlant);
    } else {
      fetchLocations(); // Load all locations when no plant selected
    }
  }, [selectedPlant, fetchLocations]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const isActiveRoute = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Menu structure based on KMR manual
  const menuItems = [
    { type: 'item', path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { type: 'divider' },
    {
      type: 'group',
      key: 'nomenclatoare',
      label: 'Nomenclatoare / Setari',
      icon: Settings,
      items: [
        { path: '/settings/symptoms', label: 'Simptome interventii', icon: AlertTriangle },
        { path: '/settings/causes', label: 'Cauze interventii', icon: HelpCircle },
        { path: '/settings/intervention-types', label: 'Tipuri interventii', icon: Wrench },
        { path: '/settings/suppliers', label: 'Furnizori servicii', icon: Truck },
        { path: '/settings/equipment-categories', label: 'Categorii echipamente', icon: Layers },
        { path: '/settings/consumable-categories', label: 'Categorii consumabile', icon: List },
        { path: '/settings/consumables', label: 'Management consumabile', icon: Package },
        { path: '/settings/equipment', label: 'Management echipamente', icon: Warehouse },
        { path: '/settings/pm-procedures', label: 'Proceduri mentenanta', icon: FileText },
        { path: '/admin/shifts', label: 'Schimburi tehnicieni', icon: Clock },
      ]
    },
    { type: 'divider' },
    { type: 'item', path: '/material-requests', label: 'Necesar consumabile', icon: ShoppingCart },
    { type: 'item', path: '/tickets', label: 'Tichete / Probleme', icon: Ticket },
    ...(['admin', 'manager', 'technician', 'section_chief'].includes(user?.role)
      ? [{ type: 'item', path: '/cross-dept-tasks', label: 'Task-uri Interdep.', icon: MessageSquare, badge: crossDeptUnread }]
      : []),
    ...(['admin', 'manager', 'technician', 'section_chief', 'auditor'].includes(user?.role)
      ? [{ type: 'item', path: '/documents-library', label: 'Biblioteca Documente', icon: FolderOpen }]
      : []),
    { type: 'divider' },
    {
      type: 'group',
      key: 'maintenance',
      label: 'Mentenanta',
      icon: Wrench,
      items: [
        { path: '/maintenance/preventive', label: 'Dashboard', icon: Calendar },
        { path: '/maintenance/preventive/entries', label: 'Preventiva', icon: ClipboardList },
        { path: '/maintenance/preventive/plan', label: 'Plan Mentenanta', icon: ClipboardList },
        { path: '/maintenance/corrective', label: 'Corectiva / Accidentala', icon: AlertTriangle },
        { path: '/maintenance/predictive', label: 'Predictiva', icon: Activity },
        { path: '/maintenance/battery-service', label: 'Service Baterii', icon: Battery },
        { path: '/maintenance/alerts-dashboard', label: 'Dashboard Alerte', icon: Bell, highlight: true },
      ]
    },
    { type: 'divider' },
    {
      type: 'group',
      key: 'wms',
      label: 'WMS - Gestiune Stocuri',
      icon: Warehouse,
      items: [
        { path: '/wms', label: 'Dashboard WMS', icon: LayoutDashboard },
        { path: '/wms/activity', label: 'Activitate Live', icon: Activity, highlight: true },
        { path: '/wms/heatmap', label: 'Heatmap Activitate', icon: Activity, highlight: true },
        { path: '/wms/smart-inventory', label: 'Inventar Inteligent', icon: ClipboardList, highlight: true },
        { path: '/wms/stock-alerts', label: 'Alerte Stoc', icon: Bell, highlight: true },
        { path: '/wms/leaderboard', label: 'Clasament Eficienta', icon: Award, highlight: true },
        { path: '/wms/cabinet-3d', label: 'Vizualizare 3D', icon: Monitor, highlight: true },
        { path: '/wms/stock-locator', label: '🔍 Localizare Stoc', icon: Package, highlight: true },
        { path: '/wms/stock-adjustment', label: '🔧 Reglare Stoc', icon: Wrench, highlight: true },
        { path: '/wms/traceability', label: '📋 Trasabilitate Articol', icon: History, highlight: true },
        { path: '/wms/receipts-dashboard', label: '📊 Dashboard Receptii', icon: BarChart3, highlight: true },
        { path: '/wms/receipts', label: '📦 Receptii Marfa', icon: Package, highlight: true },
        { path: '/wms/stock', label: 'Situatie Stoc', icon: Package },
        { path: '/wms/bins', label: 'Locatii BIN', icon: Archive },
        { path: '/wms/movements', label: 'Miscari Stoc', icon: History },
        { path: '/wms/inventory', label: 'Inventar Periodic', icon: ClipboardList },
        { path: '/wms/orders', label: 'Comenzi Furnizori', icon: ShoppingCart },
        { path: '/wms/supplier-intelligence', label: 'Comparatie Furnizori', icon: BarChart3, highlight: true },
        { path: '/wms/labels', label: 'Etichete / Rapoarte', icon: Tag },
      ]
    },
    { type: 'item', path: '/tools', label: 'Scule de Lucru', icon: Wrench },
    { type: 'divider' },
    { type: 'item', path: '/shift-handover', label: 'Registru de Schimb', icon: ClipboardList },
    {
      type: 'group',
      key: 'iscir',
      label: 'Conformitate ISCIR',
      icon: Shield,
      items: [
        { path: '/iscir', label: 'Verificari ISCIR', icon: Shield },
        { path: '/iscir/nomenclator', label: 'Nomenclator Echipamente', icon: ClipboardList },
        { path: '/iscir/categorii', label: 'Categorii ISCIR Echipamente', icon: Tags },
        { path: '/iscir/service-lize', label: 'Service Lize', icon: Wrench },
      ]
    },
    {
      type: 'group',
      key: 'emm',
      label: 'EMM-uri',
      icon: Gauge,
      items: [
        { path: '/emm', label: 'Dashboard EMM', icon: Gauge },
        { path: '/emm/nomenclator', label: 'Nomenclator Echipamente', icon: ClipboardList },
      ]
    },
    { type: 'item', path: '/lubrication', label: 'Gestionare Lubrifiere', icon: Layers },
    { type: 'divider' },
    {
      type: 'group',
      key: 'reports',
      label: 'Rapoarte & Analytics',
      icon: BarChart3,
      items: [
        { path: '/executive-dashboard', label: 'Dashboard Executiv', icon: TrendingUp },
        { path: '/reports', label: 'Rapoarte Generale', icon: FileText },
        { path: '/reports/scheduled', label: 'Rapoarte Programate', icon: Mail },
        { path: '/reports/pdf', label: 'Rapoarte PDF', icon: FileText },
        { path: '/analytics/dashboard', label: 'Analytics KPIs', icon: Activity },
        { path: '/analytics/advanced', label: 'Analitice Avansate', icon: TrendingUp },
        { path: '/analytics/inventory-forecast', label: 'Predictii Stoc', icon: TrendingUp },
        { path: '/parts-consumption', label: 'Consum Piese', icon: Package },
        { path: '/reports/ticket-consumption', label: 'Consum din Tichete', icon: FileText },
        { path: '/reports/advanced-consumption', label: 'Raport Consum Avansat', icon: BarChart3 },
        { path: '/report-builder', label: 'Report Builder', icon: Database },
      ]
    },
    { type: 'item', path: '/calendar', label: 'Calendar', icon: Calendar },
    { type: 'item', path: '/status', label: 'Status', icon: Clock },
    { type: 'divider' },
    { type: 'item', path: '/scan', label: 'Scan QR', icon: QrCode },
    { type: 'item', path: '/knowledge-base', label: 'Baza de Cunostinte', icon: BookOpen },
  ];

  // Admin menu (only for admin/manager)
  const adminMenuItems = hasRole('admin', 'manager') || (hasPermission && hasPermission('admin.full', 'settings.read')) ? [
    { type: 'divider' },
    {
      type: 'group',
      key: 'admin',
      label: 'Configurari Admin',
      icon: Shield,
      items: [
        { path: '/admin/plants', label: 'Sedii', icon: Building2 },
        { path: '/admin/locations', label: 'Locatii echipamente', icon: MapPin },
        { path: '/admin/general', label: 'Setari generale', icon: Cog },
        { path: '/admin/roles', label: 'Roluri', icon: Shield },
        { path: '/admin/users', label: 'Utilizatori', icon: Users },
        { path: '/admin/competencies', label: 'Competente', icon: Award },
        { path: '/admin/procedures', label: 'Proceduri de lucru', icon: ClipboardList },
        { path: '/admin/mail', label: 'Configurari mail', icon: Mail },
        { path: '/admin/sms', label: 'Configurari SMS', icon: Bell },
        { path: '/admin/alerts', label: 'Alerte automate', icon: Bell },
        { path: '/admin/backup', label: 'Backup BD', icon: Database },
        { path: '/admin/audit-log', label: 'Log-uri Sistem', icon: FileText },
        { path: '/admin/import-export', label: 'Import / Export', icon: FileText },
        { path: '/admin/company-data', label: 'Date Firme (ANAF)', icon: Building2 },
        { path: '/admin/badges', label: 'Ecusoane QR', icon: QrCode },
        { path: '/admin/repair-stock', label: 'Reparare Stoc', icon: Wrench },
        { path: '/admin/kiosk-devices', label: 'Dispozitive Kiosk', icon: Monitor },
        { path: '/admin/system-health', label: 'Sanatate Sistem', icon: Activity },
        { path: '/admin/recycle-bin', label: 'Cos de gunoi', icon: Trash2 },
        { path: '/admin/signatures', label: 'Semnaturi Digitale', icon: FileText },
        { path: '/admin/scheduled-notifications', label: 'Notificari Programate', icon: Clock },
        { path: '/admin/alert-settings', label: 'Alerte Sonore', icon: Volume2 },
        { type: 'divider' },
        { path: '/portal', label: 'Portal Sef Sectie', icon: Briefcase },
        { path: '/audit', label: 'Portal Auditor', icon: ClipboardCheck },
      ]
    }
  ] : [];

  const allMenuItems = [
    ...menuItems, 
    ...adminMenuItems, 
    { type: 'divider' },
    { type: 'item', path: '/quick-pick', label: 'Quick Pick', icon: Package },
    { type: 'item', path: '/kiosk', label: 'Mod TV (Kiosk)', icon: Activity },
    { type: 'item', path: '/help', label: 'Ajutor', icon: Info }
  ];

  // Mobile bottom navigation
  const mobileNavItems = [
    { path: '/dashboard', label: 'Acasa', icon: LayoutDashboard },
    { path: '/scan', label: 'Scan', icon: QrCode },
    { path: '/tickets', label: 'Tichete', icon: Ticket },
    { path: '/wms/stock-locator', label: 'Stoc', icon: Package },
    { key: 'more', label: 'Mai mult', icon: Grid3X3, action: () => setMobileMenuOpen(true) },
  ];

  // Mobile grid menu - categorized
  const mobileGridCategories = [
    {
      label: 'Operatiuni',
      items: [
        { path: '/material-requests', label: 'Necesar', icon: ShoppingCart, color: 'bg-blue-500' },
        { path: '/tickets', label: 'Tichete', icon: Ticket, color: 'bg-indigo-500' },
        ...(['admin', 'manager', 'technician', 'section_chief'].includes(user?.role)
          ? [{ path: '/cross-dept-tasks', label: 'Task-uri Interdep.', icon: MessageSquare, color: 'bg-pink-500', badge: crossDeptUnread }]
          : []),
        { path: '/scan', label: 'Scan QR', icon: QrCode, color: 'bg-violet-500' },
        { path: '/knowledge-base', label: 'Cunostinte', icon: BookOpen, color: 'bg-indigo-600' },
        { path: '/shift-handover', label: 'Schimb', icon: ClipboardList, color: 'bg-cyan-500' },
      ]
    },
    {
      label: 'Mentenanta',
      items: [
        { path: '/maintenance/preventive', label: 'Dashboard', icon: Calendar, color: 'bg-emerald-500' },
        { path: '/maintenance/preventive/entries', label: 'Preventiva', icon: ClipboardList, color: 'bg-green-500' },
        { path: '/maintenance/corrective', label: 'Corectiva', icon: AlertTriangle, color: 'bg-amber-500' },
        { path: '/maintenance/battery-service', label: 'Baterii', icon: Battery, color: 'bg-yellow-500' },
        { path: '/maintenance/alerts-dashboard', label: 'Alerte', icon: Bell, color: 'bg-red-500' },
        { path: '/maintenance/predictive', label: 'Predictiva', icon: Activity, color: 'bg-teal-500' },
      ]
    },
    {
      label: 'WMS - Stocuri',
      items: [
        { path: '/wms', label: 'Dashboard', icon: LayoutDashboard, color: 'bg-blue-600' },
        { path: '/wms/stock-locator', label: 'Unde este?', icon: Search, color: 'bg-purple-500' },
        { path: '/wms/stock-adjustment', label: 'Reglare Stoc', icon: Wrench, color: 'bg-amber-600' },
        { path: '/wms/receipts', label: 'Receptii', icon: Package, color: 'bg-sky-500' },
        { path: '/wms/stock', label: 'Stoc', icon: Package, color: 'bg-slate-500' },
        { path: '/wms/bins', label: 'Locatii BIN', icon: Archive, color: 'bg-stone-500' },
        { path: '/wms/movements', label: 'Miscari', icon: History, color: 'bg-zinc-500' },
        { path: '/wms/supplier-intelligence', label: 'Furnizori', icon: BarChart3, color: 'bg-orange-500' },
        { path: '/wms/stock-alerts', label: 'Alerte Stoc', icon: Bell, color: 'bg-rose-500' },
      ]
    },
    {
      label: 'ISCIR & EMM',
      items: [
        { path: '/iscir', label: 'Verificari', icon: Shield, color: 'bg-indigo-600' },
        { path: '/iscir/service-lize', label: 'Service Lize', icon: Wrench, color: 'bg-blue-400' },
        { path: '/emm', label: 'EMM', icon: Gauge, color: 'bg-fuchsia-500' },
        { path: '/lubrication', label: 'Lubrifiere', icon: Layers, color: 'bg-amber-600' },
      ]
    },
    {
      label: 'Nomenclatoare',
      items: [
        { path: '/settings/consumables', label: 'Consumabile', icon: Package, color: 'bg-emerald-600' },
        { path: '/settings/equipment', label: 'Echipamente', icon: Warehouse, color: 'bg-cyan-600' },
        { path: '/settings/suppliers', label: 'Furnizori', icon: Truck, color: 'bg-violet-600' },
        { path: '/tools', label: 'Scule', icon: Wrench, color: 'bg-gray-500' },
      ]
    },
    {
      label: 'Rapoarte',
      items: [
        { path: '/executive-dashboard', label: 'Executiv', icon: TrendingUp, color: 'bg-rose-600' },
        { path: '/reports', label: 'Rapoarte', icon: FileText, color: 'bg-slate-600' },
        { path: '/analytics/advanced', label: 'Analitice', icon: BarChart3, color: 'bg-purple-600' },
        { path: '/parts-consumption', label: 'Consum', icon: Package, color: 'bg-teal-600' },
      ]
    },
  ];

  const renderMenuItem = (item, index) => {
    if (item.type === 'divider') {
      return <div key={index} className="h-px bg-slate-700 my-2 mx-2" />;
    }

    if (item.type === 'group') {
      const isExpanded = expandedMenus[item.key];
      const hasActiveChild = item.items.some(child => isActiveRoute(child.path));
      const showBadge = item.key === 'maintenance' && pmOverdueCount > 0;
      
      return (
        <Collapsible key={item.key} open={isExpanded} onOpenChange={() => toggleMenu(item.key)}>
          <CollapsibleTrigger className={`w-full flex items-center justify-between px-4 py-2.5 text-sm rounded-md transition-colors ${
            hasActiveChild ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
          }`}>
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {showBadge && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full animate-pulse" data-testid="pm-overdue-badge">
                  {pmOverdueCount}
                </span>
              )}
            </div>
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4 mt-1 space-y-1">
            {item.items.map((subItem, subIndex) => {
              if (subItem.type === 'divider') {
                return <div key={subIndex} className="h-px bg-slate-700 my-2 mx-2" />;
              }
              const subBadge = subItem.path === '/maintenance/battery-service' && batteryAlertCount > 0;
              return (
              <NavLink
                key={subIndex}
                to={subItem.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors ${
                    isActive ? 'bg-[#0052CC] text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`
                }
              >
                <subItem.icon className="w-4 h-4" />
                <span className="truncate">{subItem.label}</span>
                {subBadge && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[9px] font-bold bg-red-500 text-white rounded-full" data-testid="battery-alert-badge">
                    {batteryAlertCount}
                  </span>
                )}
              </NavLink>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    return (
      <NavLink
        key={index}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-2.5 text-sm rounded-md transition-colors ${
            isActive ? 'bg-[#0052CC] text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
          }`
        }
      >
        <item.icon className="w-5 h-5" />
        <span className="flex-1">{item.label}</span>
        {item.badge > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-rose-500 text-white rounded-full" data-testid={`menu-badge-${item.path.replace(/\//g, '-').slice(1)}`}>
            {item.badge}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 bg-slate-900 text-white overflow-hidden">
        <div className="flex flex-col flex-1 overflow-y-auto no-scrollbar">
          {/* Logo */}
          <div className="flex items-center h-16 px-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0052CC] rounded-lg flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight">MAINTIX</h1>
                <p className="text-[10px] text-slate-400 -mt-0.5">Smart Maintenance & Asset Control</p>
              </div>
            </div>
          </div>

          {/* Global Filters */}
          <div className="px-3 py-3 border-b border-slate-700 space-y-2 flex-shrink-0">
            <Select value={selectedPlant || 'all'} onValueChange={setSelectedPlant}>
              <SelectTrigger className="w-full bg-slate-800 border-slate-600 text-white text-sm h-9">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Toate sediile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate sediile</SelectItem>
                {plants.map((plant) => (
                  <SelectItem key={plant.id} value={plant.id}>{plant.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedLocation || 'all'} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-full bg-slate-800 border-slate-600 text-white text-sm h-9">
                <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Toate locatiile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate locatiile</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quick Stock Search Widget */}
          <div className="px-2 py-2 border-b border-slate-700 flex-shrink-0">
            <QuickStockSearch />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto no-scrollbar">
            {allMenuItems.map((item, index) => renderMenuItem(item, index))}
          </nav>

          {/* Quick Add Button */}
          <div className="px-3 py-2 border-t border-slate-700 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="w-full bg-[#0052CC] hover:bg-[#0052CC]/90 h-10">
                  <Plus className="w-4 h-4 mr-2" />
                  Adauga situatie noua
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/material-requests?add=true')}>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Necesar consumabile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/tickets?add=true')}>
                  <Ticket className="w-4 h-4 mr-2" />
                  Tichet nou
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings/equipment?add=true')}>
                  <Package className="w-4 h-4 mr-2" />
                  Echipament nou
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/maintenance/preventive?add=true')}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Mentenanta preventiva
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/maintenance/corrective?add=true')}>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Mentenanta corectiva
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/maintenance/predictive?add=true')}>
                  <Activity className="w-4 h-4 mr-2" />
                  Mentenanta predictiva
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* User Info */}
          <div className="p-3 border-t border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0052CC] flex items-center justify-center text-white font-semibold text-sm">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-600 text-center mt-2 tracking-wide">Conceput si dezvoltat de Daniel Morosanu</p>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-40 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="w-6 h-6" />
        </Button>
        <div className="flex-1 flex items-center justify-center">
          <h1 className="text-lg font-bold text-[#0052CC]">MAINTIX</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative" data-testid="ws-status-mobile" title={wsConnected ? 'Conectat in timp real' : 'Deconectat'}>
            <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`} />
          </div>
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <div className="w-8 h-8 rounded-full bg-[#0052CC] flex items-center justify-center text-white text-sm font-semibold">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <Users className="w-4 h-4 mr-2" />
                Profilul Meu
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/about')}>
                <Info className="w-4 h-4 mr-2" />
                Info
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Deconectare
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-slate-900 overflow-y-auto animate-slide-up">
            <div className="flex items-center justify-between h-14 px-4 border-b border-slate-700">
              <h1 className="text-lg font-bold text-white">MAINTIX</h1>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="text-white">
                <X className="w-6 h-6" />
              </Button>
            </div>
            <div className="px-3 py-3 border-b border-slate-700 space-y-2">
              <Select value={selectedPlant || 'all'} onValueChange={setSelectedPlant}>
                <SelectTrigger className="w-full bg-slate-800 border-slate-600 text-white text-sm">
                  <SelectValue placeholder="Toate sediile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate sediile</SelectItem>
                  {plants.map((plant) => (
                    <SelectItem key={plant.id} value={plant.id}>{plant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <nav className="px-2 py-3 space-y-1">
              {allMenuItems.map((item, index) => renderMenuItem(item, index))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Header Bar */}
      <header className="hidden lg:flex fixed top-0 left-72 right-0 h-14 bg-white border-b border-slate-200 z-30 items-center justify-end px-6 gap-4">
        <div className="flex items-center gap-1.5" data-testid="ws-status-desktop" title={wsConnected ? 'Conectat in timp real' : 'Deconectat - se reconecteaza...'}>
          <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`} />
          <span className={`text-[10px] font-medium ${wsConnected ? 'text-emerald-600' : 'text-red-500'}`}>
            {wsConnected ? 'Live' : 'Offline'}
          </span>
        </div>
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0052CC] flex items-center justify-center text-white text-sm font-semibold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <span className="text-sm font-medium text-slate-700">{user?.full_name}</span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <Users className="w-4 h-4 mr-2" />
              Profilul Meu
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/about')}>
              <Info className="w-4 h-4 mr-2" />
              Info
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main Content */}
      <main className="lg:pl-72 pt-14 pb-16 lg:pb-0 min-h-screen overflow-x-hidden">
        <div className="px-3 py-3 md:p-6">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav lg:hidden flex items-center justify-evenly py-1.5" data-testid="mobile-bottom-nav">
        {mobileNavItems.map((item) => (
          item.action ? (
            <button
              key={item.key}
              onClick={item.action}
              className={`relative flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg ${mobileMenuOpen ? 'text-[#0052CC]' : 'text-slate-500'}`}
              data-testid="mobile-more-btn"
            >
              <div className="relative">
                <item.icon className="w-5 h-5" />
                {crossDeptUnread > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
                    data-testid="mobile-more-badge"
                  >
                    {crossDeptUnread > 99 ? '99+' : crossDeptUnread}
                  </span>
                )}
              </div>
              <span className="text-[9px] font-medium">{item.label}</span>
            </button>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg ${
                  isActive && !mobileMenuOpen ? 'text-[#0052CC]' : 'text-slate-500'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[9px] font-medium">{item.label}</span>
            </NavLink>
          )
        ))}
      </nav>

      {/* Mobile Full-Screen Grid Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white animate-in fade-in duration-200" data-testid="mobile-grid-menu">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14 z-10">
            <h2 className="text-lg font-bold text-slate-900">Meniu</h2>
            <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-full hover:bg-slate-100">
              <X className="w-6 h-6 text-slate-500" />
            </button>
          </div>
          {/* Quick Actions */}
          <div className="px-4 pt-3 pb-2">
            <div className="flex gap-2">
              <button onClick={() => { navigate('/tickets?add=true'); setMobileMenuOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 h-11 bg-[#0052CC] text-white rounded-xl text-sm font-semibold active:scale-95 transition-transform"
                data-testid="mobile-quick-ticket">
                <Plus className="w-4 h-4" /> Tichet Nou
              </button>
              <button onClick={() => { navigate('/material-requests?add=true'); setMobileMenuOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 h-11 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
                data-testid="mobile-quick-request">
                <ShoppingCart className="w-4 h-4" /> Necesar
              </button>
            </div>
          </div>
          {/* Scrollable Grid Categories */}
          <div className="overflow-y-auto px-4 pb-24" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            {mobileGridCategories.map((cat) => (
              <div key={cat.label} className="mt-4">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">{cat.label}</p>
                <div className="grid grid-cols-4 gap-2">
                  {cat.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `relative flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-all active:scale-95 ${
                          isActive ? 'bg-blue-50 ring-2 ring-blue-200' : 'bg-slate-50 hover:bg-slate-100'
                        }`
                      }
                      data-testid={`mobile-menu-${item.path.replace(/\//g, '-').slice(1)}`}
                    >
                      <div className={`relative w-10 h-10 rounded-xl ${item.color} flex items-center justify-center`}>
                        <item.icon className="w-5 h-5 text-white" />
                        {item.badge > 0 && (
                          <span
                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
                            data-testid={`mobile-menu-badge-${item.path.replace(/\//g, '-').slice(1)}`}
                          >
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
            {/* Admin section for admin/manager */}
            {(hasRole('admin', 'manager') || hasPermission('admin.full', 'settings.read')) && (
              <div className="mt-4">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Administrare</p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { path: '/admin/users', label: 'Utilizatori', icon: Users, color: 'bg-slate-700' },
                    { path: '/admin/plants', label: 'Sedii', icon: Building2, color: 'bg-slate-600' },
                    { path: '/admin/general', label: 'Setari', icon: Cog, color: 'bg-slate-500' },
                    { path: '/admin/audit-log', label: 'Log-uri', icon: FileText, color: 'bg-slate-400' },
                  ].map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl bg-slate-50 hover:bg-slate-100 active:scale-95 transition-all"
                    >
                      <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center`}>
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
            {/* User & other links */}
            <div className="mt-6 mb-4 border-t border-slate-100 pt-4 space-y-2">
              <NavLink to="/profile" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50">
                <div className="w-9 h-9 rounded-full bg-[#0052CC] flex items-center justify-center text-white text-sm font-semibold">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </NavLink>
              <NavLink to="/help" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 text-slate-600">
                <Info className="w-5 h-5" /> <span className="text-sm">Ajutor</span>
              </NavLink>
              <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 text-red-600">
                <LogOut className="w-5 h-5" /> <span className="text-sm">Deconectare</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Splash Screen - Big overlay for important events */}
      <AlertSplashScreen />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
};

export default MainLayout;
